import React, { useState, useEffect, useRef } from "react";
import "./styles/RearrangeSeat.css"; // ✅ Updated CSS Import
import { apiService } from "./services/apiService"; 

function RearrangeSeat({ onSuccess }) {
  const [searchValue, setSearchValue] = useState("");
  const [admissions, setAdmissions] = useState([]);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [seats, setSeats] = useState([]);
  const [selectedSeatId, setSelectedSeatId] = useState(null);
  const [searched, setSearched] = useState(false);

  const originalSeatRef = useRef(null);

  // ================= SEARCH =================
  const search = async () => {
    try {
      const data = await apiService.searchAdmissions(searchValue);

      if (!data || data.length === 0) {
        alert("❌ Admission not found");
        setAdmissions([]);
        setSelectedAdmission(null);
        setSearched(true);
        return;
      }

      originalSeatRef.current = null;
      const first = data[0];

      setAdmissions(data);
      setSelectedAdmission(first);
      setSearched(true);

      if (!originalSeatRef.current) {
        originalSeatRef.current = first.seat?.seatCode;
      }

      setSelectedSeatId(null);
    } catch (err) {
      console.error(err);
      alert("❌ Search failed");
      setSearched(true);
    }
  };

  // ================= LOAD SEATS =================
  useEffect(() => {
    apiService.getAllSeats()
      .then(setSeats)
      .catch((err) => console.error("Error loading seat inventory maps:", err));
  }, []);

  // ================= SEAT COLOR =================
  const getSeatColor = (seat) => {
    if (seat.blocked) return "#9e9e9e";
    if (seat.status === "RENEWAL_PENDING") return "#ffc107";
    if (seat.currentCount === 0) return "#64b5f6";
    if (seat.currentCount === 1) return "#81c784";
    if (seat.currentCount >= seat.maxCapacity) return "#2e7d32";
    return "#64b5f6";
  };

  // ================= SELECT SEAT =================
  const handleSeatClick = (seat) => {
    if (!selectedAdmission) {
      alert("Please select a student first");
      return;
    }
    if (seat.currentCount !== 0) {
      alert("Only completely empty seats allowed");
      return;
    }
    setSelectedSeatId(seat.seatId);
  };

  // ================= REARRANGE =================
  const rearrangeSeat = async () => {
    if (!selectedSeatId) {
      alert("Please select a new seat");
      return;
    }
    if (!window.confirm("Confirm seat rearrangement?")) return;

    try {
      const updated = await apiService.amendSeatAssignment(selectedAdmission.id, selectedSeatId);

      setAdmissions([updated]);
      setSelectedAdmission(updated);
      setSelectedSeatId(null);

      alert("✅ Seat rearranged successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      alert("❌ Error rearranging seat");
    }
  };

  const newSeatCode = selectedSeatId
    ? seats.find((s) => s.seatId === selectedSeatId)?.seatCode
    : selectedAdmission?.seat?.seatCode;

  return (
    <div className="ras-page">

      {/* ── Header card ── */}
      <div className="ras-header-card">
        <div className="ras-header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2">
            <path d="M21 10H3"/>
            <path d="M21 6H3"/>
            <path d="M21 14H3"/>
            <path d="M21 18H3"/>
          </svg>
        </div>
        <div>
          <h2 className="ras-title">Rearrange Seat</h2>
          <p className="ras-subtitle">Search a student to change their assigned seat</p>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="ras-search-row">
        <input
          className="ras-search-input"
          type="text"
          placeholder="Search by Name / Mobile / Seat / Registration No"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <button className="ras-search-btn" onClick={search}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          Search
        </button>
      </div>

      {/* ── No results ── */}
      {searched && !selectedAdmission && (
        <div className="ras-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>No admissions found. Try a different search.</p>
        </div>
      )}

      {selectedAdmission && (
        <div className="ras-card">

          {/* Student info header */}
          <div className="ras-card-header">
            <div className="ras-avatar">
              {selectedAdmission.student?.fullName ? selectedAdmission.student.fullName.charAt(0).toUpperCase() : "?"}
            </div>
            <div>
              <div className="ras-student-name">{selectedAdmission.student?.fullName}</div>
              <div className="ras-student-sub">{selectedAdmission.student?.primaryContact}</div>
            </div>
            <div className="ras-seat-badge">{originalSeatRef.current}</div>
          </div>

          <div className="ras-divider" />

          {/* Details grid */}
          <div className="ras-details-grid">
            <div className="ras-detail-item">
              <span className="ras-detail-label">Old Seat</span>
              <span className="ras-detail-value">{originalSeatRef.current}</span>
            </div>
            <div className="ras-detail-item">
              <span className="ras-detail-label">New Seat</span>
              <span className={`ras-detail-value ${selectedSeatId ? "ras-new-seat-value" : ""}`}>
                {newSeatCode}
              </span>
            </div>
            <div className="ras-detail-item">
              <span className="ras-detail-label">Expiry Date</span>
              <span className="ras-detail-value">
                {selectedAdmission.endDate ? new Date(selectedAdmission.endDate).toLocaleDateString("en-GB") : "N/A"}
              </span>
            </div>
          </div>

          <div className="ras-divider" />

          {/* Seat grid section */}
          <div className="ras-section-label">
            <span className="ras-section-dot" />
            Select Unoccupied New Seat
          </div>

          <div className="ras-seat-grid">
            {seats.map((seat) => (
              <button
                key={seat.seatId}
                onClick={() => handleSeatClick(seat)}
                disabled={seat.currentCount !== 0}
                className={`ras-seat-btn ${selectedSeatId === seat.seatId ? "selected" : ""}`}
                style={{ backgroundColor: getSeatColor(seat) }}
              >
                {seat.seatCode}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="ras-actions">
            <button
              className="ras-btn ras-btn-update"
              disabled={!selectedSeatId}
              onClick={rearrangeSeat}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Rearrange Seat
            </button>
          </div>

        </div>
      )}

    </div>
  );
}

export default RearrangeSeat;